// ===== Sync: loading, reloading and writing the shared data =====
// The decisions live here rather than in MedicationApp so Node can test them
// (tests/sync.test.js). Nothing in this file imports the Supabase client or
// touches the browser: the store functions are passed in (`store` = the
// exports of data/store.js), so a test can hand in fakes.

// ----- the in-memory lists (doses, temperature readings) -----
// Both lists are kept newest-first: the safety checks take the first match as
// the most recent dose.
export const byNewestFirst = (a, b) => new Date(b.timestamp) - new Date(a.timestamp);

// A new entry is the newest, so it goes on top without a sort.
export const addEntry = (list, entry) => [entry, ...list];
export const removeEntry = (list, id) => list.filter(e => e.id !== id);
// Undo of a delete: the same entry (same id) goes back in its newest-first
// place, never twice.
export const restoreEntry = (list, entry) => [...removeEntry(list, entry.id), entry].sort(byNewestFirst);
// An edit can change the timestamp, and so the entry's place in the order.
export const updateEntry = (list, id, updates) =>
  list.map(e => (e.id === id ? { ...e, ...updates } : e)).sort(byNewestFirst);

// Children that were in the list before a save and aren't any more: their
// rows are deleted.
export const removedIds = (before, after) => {
  const kept = new Set(after.map(c => c.id));
  return before.map(c => c.id).filter(id => !kept.has(id));
};

// ----- loading -----
// Legacy single-row children carry number ids, which can't be a row id; give
// those a UUID and keep ids that already are one.
export const withRowIds = (children, newId = () => crypto.randomUUID()) =>
  children.map(c => (typeof c.id === 'string' && c.id.includes('-') ? c : { ...c, id: newId() }));

// Every change on either phone triggers a full reload, and a burst of them (an
// Undo, a batch of "Other" medicines) overlaps. Only the newest reload may
// apply its result — an older fetch finishing last would otherwise put back
// what a newer one had already removed, or drop what it added.
//
//   apply(data)       the decoded data, for the screens
//   setStatus(s)      'synced' | 'offline' (showing the cached copy) | 'error'
//   canMigrate()      true once there's a key to encrypt the migrated rows with
export function createReloader({ store, apply, setStatus, canMigrate = () => true }) {
  let active = true;
  let latestReload = 0;
  let migrating = false;
  const show = (d) => { if (active) apply(d); };
  const status = (s) => { if (active) setStatus(s); };

  // One-time migration: convert legacy single-row children to one row each.
  // The old row is deleted only after the new rows are safely written; a
  // failure leaves it in place to try again on the next reload.
  const migrateChildren = async (d) => {
    if (!d.legacyChildRowId || migrating || !canMigrate()) return;
    migrating = true;
    try {
      for (const child of withRowIds(d.children)) await store.upsertChildRow(child);
      await store.deleteRow(d.legacyChildRowId);
    } catch (e) { console.error('Child migration failed:', e); migrating = false; }
  };

  const reload = async () => {
    const mine = ++latestReload;
    try {
      const rows = await store.fetchAllRows();
      if (mine !== latestReload) return;
      store.cacheRows(rows);
      const d = await store.decodeRows(rows);
      if (mine !== latestReload) return;
      show(d);
      status('synced');
      await migrateChildren(d);
    } catch (e) {
      console.error('Sync load failed:', e);
      if (mine !== latestReload) return;
      // No connection: show the last copy fetched (ciphertext, kept by cacheRows).
      const cached = store.readCachedRows();
      if (cached) {
        try {
          const d = await store.decodeRows(cached);
          if (mine !== latestReload) return;
          show(d);
          status('offline');
          return;
        } catch (e2) {}
      }
      status('error');
    }
  };

  // After stop() (the screen unmounted), nothing is applied any more.
  return { reload, stop: () => { active = false; } };
}

// Load now, and reload on every change to the table (from either phone).
// Returns the cleanup for the effect that starts it.
export function startSync(options) {
  const { store } = options;
  const { reload, stop } = createReloader(options);
  reload();
  const channel = store.sb.channel('entries-sync')
    .on('postgres_changes', { event: '*', schema: 'public', table: 'entries' }, reload)
    .subscribe();
  return () => { stop(); store.sb.removeChannel(channel); };
}

// ----- writing -----
// In-flight deletes by row id, so an Undo can wait for its row's delete to
// land before re-inserting it.
export function createDeleteTracker() {
  const pending = new Map();
  return {
    track(id, promise) {
      pending.set(id, promise);
      // A later delete of the same id replaces this one; only clear our own.
      promise.finally(() => { if (pending.get(id) === promise) pending.delete(id); });
    },
    after: (id) => pending.get(id) || Promise.resolve(),
  };
}

// The cloud side of every change the screens make. The screens' state is
// updated first, by the caller; these only write. A failed write is reported
// through onFail and not retried. Pass store = null (DEV_MODE) and every
// write is skipped.
export function createCloudWriter(store, onFail) {
  if (!store) {
    const skip = () => {};
    return { saveChildren: skip, saveConcentrations: skip, saveOtherMeds: skip, addHistory: skip, updateHistory: skip, restoreHistory: skip, addTemperature: skip, updateTemperature: skip, restoreTemperature: skip, deleteEntry: skip };
  }
  const deletes = createDeleteTracker();
  const run = (promise) => promise.catch(onFail);
  // The re-insert waits for that row's delete to finish, or an Undo tapped
  // while the delete is still in flight could be inserted first and then
  // deleted.
  const reinsert = (insert, entry) => run(deletes.after(entry.id).then(() => insert(entry)));

  return {
    // Children: one row each. Upsert every child (idempotent) and delete any
    // that were removed, so concurrent edits on two phones don't clobber.
    saveChildren: (before, after) => run((async () => {
      for (const c of after) await store.upsertChildRow(c);
      for (const id of removedIds(before, after)) await store.deleteRow(id);
    })()),
    // Settings kinds are one encrypted row each (rarely edited).
    saveConcentrations: (data) => run(store.saveKindRow('concentration', data)),
    saveOtherMeds: (data) => run(store.saveKindRow('otherMed', data)),
    // History and temperature are append-only, one row per entry, so two
    // phones logging at the same time never clobber each other. An edit
    // overwrites the entry's own row.
    addHistory: (entry) => run(store.insertHistoryRow(entry)),
    updateHistory: (entry) => run(store.updateHistoryRow(entry)),
    restoreHistory: (entry) => reinsert(store.insertHistoryRow, entry),
    addTemperature: (entry) => run(store.insertTemperatureRow(entry)),
    updateTemperature: (entry) => run(store.updateTemperatureRow(entry)),
    restoreTemperature: (entry) => reinsert(store.insertTemperatureRow, entry),
    // Doses and readings are deleted the same way: by row id.
    deleteEntry: (id) => deletes.track(id, run(store.deleteRow(id))),
  };
}
